import React, { useEffect, useState } from 'react';

import { CircleFilled, ArrowRight, CheckmarkFilled, InformationFilled, WarningFilled } from '@carbon/icons-react';
import { IconOnlyButton, AdvancedProgressBar, Tag, Button, Intent, HTMLHeading } from '@varicent/components';

import TextButton from 'components/Buttons/TextButton/TextButton';
import DropdownMenu from 'components/DropdownMenu/DropdownMenu';
import DropdownMenuDivider from 'components/DropdownMenu/DropdownMenuDivider';
import DropdownMenuHeading from 'components/DropdownMenu/DropdownMenuHeading';
import { TemporaryActivityImportMaintenanceBanner } from 'components/MaintenanceBanner/MaintenanceBanner';
//eslint-disable-next-line no-restricted-imports
import MessageTooltip from 'components/MessageTooltip/MessageTooltip';
import Tab from 'components/Tab/Tab';
import Tabs from 'components/Tabs/Tabs';
import Timer from 'components/Timer/Timer';
import ToastMessage from 'components/ToastMessage/ToastMessage';

import { useCoinsort } from 'app/contexts/coinsortProvider';
import { useData } from 'app/contexts/dataProvider';
import { useJobs } from 'app/contexts/jobsProvider';
import { useScope } from 'app/contexts/scopeProvider';
import { useTerritoryDefineAndRefine } from 'app/contexts/territoryDefineAndRefineProvider';

import { SplitFeatures } from 'app/global/features';

import { FileTypeEnum } from 'app/graphql/generated/graphqlApolloTypes';
import { useGetFileListForStatusMenuLazy } from 'app/graphql/queries/getFileList';

import useNavigateToTab from 'app/hooks/useNavigateToTab';
import useShowToast from 'app/hooks/useShowToast';
import useTreatment from 'app/hooks/useTreatment';

import { FileStatus, JobStatus, JobType, StatusMenuTab } from 'app/models';

import block from 'utils/bem-css-modules';
import { formatMessage, formatNumber } from 'utils/messages/utils';

import taskSvg from 'assets/svgs/task-optimization.svg';

import style from './StatusMenu.module.pcss';
import { checkWithinLast7Days, FileTypeMap, getFormattedTime, getJobItemDetail, sortJobItems } from './statusMenuUtils';

const b = block(style);

const ArrowRightIcon = () => <ArrowRight size={24} />;

class StatusInfoHelper {
  static getDisplayedInfoBasedOnStatus(item, isCoinSort = false) {
    const battleCardName = item.metadata ? JSON.parse(item.metadata)?.battlecardName : '';
    if (item.status === JobStatus.COMPLETED) {
      return {
        intent: Intent.SUCCESS,
        icon: <CheckmarkFilled />,
        header: isCoinSort
          ? formatMessage('STATUS_MENU_COINSORT_COMPLETED', { battleCardName })
          : formatMessage('IMPORTED', { fileName: item.tableName })
      };
    } else if (item.status === JobStatus.FAILED) {
      return {
        intent: Intent.DANGER,
        icon: <WarningFilled />,
        header: isCoinSort
          ? formatMessage('STATUS_MENU_COINSORT_FAILED', { battleCardName })
          : formatMessage('FAILED_TO_IMPORT', { fileName: item.tableName })
      };
    } else {
      return {
        intent: Intent.PRIMARY,
        icon: <InformationFilled />,
        header: isCoinSort
          ? formatMessage('STATUS_MENU_COINSORT_IN_PROGRESS', { battleCardName })
          : formatMessage('IMPORTING', { fileName: item.tableName })
      };
    }
  }
}

const emptyScreen = (jobType: JobType) => (
  <>
    {jobType === JobType.IMPORT && (
      <div className={b('maintenanceBanner')}>
        <TemporaryActivityImportMaintenanceBanner
          data-testid="maintenance-banner"
          messageKey={'MAINTENANCE_MESSAGE_STATUS_MENU'}
        />
      </div>
    )}
    <div className={b('container')} data-testid="status-menu-empty-task">
      <div className={b('imageContainer')}>
        <img src={taskSvg} alt="all-task-completed" />
      </div>
      <div className={b('textContainer')}>
        <HTMLHeading bold tagLevel="h6" text={formatMessage('ALL_DONE')} />
        {jobType === JobType.IMPORT
          ? formatMessage('NO_IMPORT_WITHIN_LAST_WEEK')
          : formatMessage('ALL_TASKS_COMPLETED')}
      </div>
    </div>
  </>
);

const FileMenu = (fileUploadItems, navigateToErrorTab) => (
  <>
    {fileUploadItems.length === 0 ? (
      emptyScreen(JobType.IMPORT)
    ) : (
      <div className={b('menu')}>
        {fileUploadItems.length === 20 && (
          <div className={b('limitInfoMessage')}>{formatMessage('SHOWING_20_MOST_RECENT_IMPORTS')}</div>
        )}

        <div className={b('maintenanceBanner')}>
          <TemporaryActivityImportMaintenanceBanner
            data-testid="maintenance-banner"
            messageKey={'MAINTENANCE_MESSAGE_STATUS_MENU'}
          />
        </div>

        {fileUploadItems.map((file) => {
          const { intent, header, icon } = StatusInfoHelper.getDisplayedInfoBasedOnStatus(file, false);
          const startTime = new Date(file.createdAt);
          const endTime = file.updatedAt ? new Date(file.updatedAt) : null;
          const isInProgress =
            file.status === FileStatus.CHECKING_PROGRESS ||
            file.status === FileStatus.PENDING_VALIDATION ||
            file.status === FileStatus.IN_PROGRESS ||
            file.status === FileStatus.VALIDATING;
          const actorFullName = file.user?.firstName.concat(' ', file.user.lastName) ?? '';

          return (
            <div key={file.tableId}>
              <div className={b('menuItem')}>
                <div className={b('menuItemHeader')}>
                  <Tag minimal icon={icon} intent={intent} />
                  <div className={b('fileName')}>
                    <HTMLHeading tagLevel={'h6'} text={header} data-testid="item-header" />
                  </div>
                </div>
                <div className={b('menuItemContent')}>
                  {isInProgress && (
                    <div className={b('progressBar')} data-testid="item-progress-bar">
                      <AdvancedProgressBar
                        value={0.5}
                        showPercentage={false}
                        intent={intent}
                        formatNumber={(progress) => formatNumber(progress, { style: 'percent' })}
                      />
                    </div>
                  )}

                  <div className={b('timeDescription')} data-testid="status-menu-time-description">
                    {isInProgress && formatMessage('TIME_STARTED', { time: getFormattedTime(startTime) })}

                    <div className={b('timer')} data-testid="status-menu-timer">
                      {!isInProgress && formatMessage('TIME_COMPLETED', { time: getFormattedTime(endTime) })}
                    </div>
                  </div>

                  <div className={b('tags')}>
                    <MessageTooltip
                      content={formatMessage('STARTED_BY', {
                        userName: actorFullName
                      })}
                      usePortal={false}
                      target={
                        <div className={b('tag')} data-testid="status-menu-user-tag">
                          {file.user && <Tag className={'vds-highlight-10'}>{file.user.emailAddress}</Tag>}
                        </div>
                      }
                      placement={'top'}
                    />

                    <MessageTooltip
                      content={formatMessage('FILE_TYPE')}
                      usePortal={false}
                      target={
                        <div className={b('tag')} data-testid="status-menu-file-type-tag">
                          <Tag className={'vds-highlight-04'}>{FileTypeMap[file.tableDataType]}</Tag>
                        </div>
                      }
                      placement={'top'}
                    />
                  </div>
                  {file.status === FileStatus.FAILED && (
                    <div className={b('actionButton')}>
                      <TextButton
                        text={formatMessage('GO_TO_IMPORT_ERRORS')}
                        minimal
                        type="button"
                        rightIcon={<ArrowRight />}
                        onClick={navigateToErrorTab}
                        testId={'go-to-errors-button'}
                      />
                    </div>
                  )}
                </div>
              </div>
              <DropdownMenuDivider />
            </div>
          );
        })}
      </div>
    )}
  </>
);

const CoinSortMenu = (jobItems) => (
  <>
    {jobItems.length === 0 ? (
      emptyScreen(JobType.COIN_SORT)
    ) : (
      <div className={b('menu')}>
        {sortJobItems(jobItems).map((item) => {
          const { intent, header, icon } = StatusInfoHelper.getDisplayedInfoBasedOnStatus(item, true);
          const { startTime, endTime, isInProgress, metaData, actorFullName } = getJobItemDetail(item);

          return (
            <div key={item.id}>
              <div className={b('menuItem')}>
                <div className={b('menuItemHeader')}>
                  <Tag minimal icon={icon} intent={intent} />
                  <HTMLHeading tagLevel={'h6'} text={header} data-testid="item-header" />
                </div>
                <div className={b('menuItemContent')}>
                  {isInProgress && (
                    <div className={b('progressBar')}>
                      <AdvancedProgressBar
                        value={item.progress / 100}
                        showPercentage
                        intent={intent}
                        formatNumber={(progress) => formatNumber(progress, { style: 'percent' })}
                      />
                    </div>
                  )}

                  <div className={b('timeDescription')} data-testid="status-menu-time-description">
                    {isInProgress && `${formatMessage('STARTED')}`}

                    <div className={b('timer')} data-testid="status-menu-timer">
                      <Timer time={isInProgress ? startTime : endTime} />
                    </div>
                  </div>

                  <div className={b('tags')}>
                    <MessageTooltip
                      content={formatMessage('STARTED_BY', {
                        userName: actorFullName
                      })}
                      usePortal={false}
                      target={
                        <div className={b('tag')} data-testid="status-menu-user-tag">
                          <Tag className={'vds-highlight-10'}>{item.actorEmail}</Tag>
                        </div>
                      }
                      placement={'top'}
                    />
                    {metaData?.deploymentModelName && (
                      <MessageTooltip
                        content={formatMessage('SCENARIO')}
                        usePortal={false}
                        target={
                          <div className={b('tag')} data-testid="status-menu-scenario-tag">
                            <Tag className={'vds-highlight-03'}>{metaData.deploymentModelName}</Tag>
                          </div>
                        }
                        placement={'top'}
                      />
                    )}

                    <MessageTooltip
                      content={formatMessage('QUOTA_COMPONENT')}
                      usePortal={false}
                      target={
                        <div className={b('tag')} data-testid="status-menu-quota-component-tag">
                          <Tag className={'vds-highlight-03'}>{metaData?.quotaComponentName}</Tag>
                        </div>
                      }
                      placement={'top'}
                    />

                    <MessageTooltip
                      content={formatMessage('BATTLE_CARD')}
                      usePortal={false}
                      target={
                        <div className={b('tag')} data-testid="status-menu-battle-card-tag">
                          <Tag className={'vds-highlight-04'}>{metaData?.battlecardName}</Tag>
                        </div>
                      }
                      placement={'top'}
                    />
                  </div>
                </div>
              </div>
              <DropdownMenuDivider />
            </div>
          );
        })}
      </div>
    )}
  </>
);

const StatusMenu: React.FC = () => {
  const [activeTabPanel, setActiveTabPanel] = useState<StatusMenuTab>(StatusMenuTab.IMPORTS);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  const { pollingJobs, jobItems } = useJobs();
  const { coinsortRunningLookupMap, updateCoinsortRunningLookupMap, updateCoinsortProgressLookupMap } = useCoinsort();
  const { getTDR } = useTerritoryDefineAndRefine();
  const { selectedPlanningCycle } = useScope();
  const { fileUploadInProgress, setFileUploadInProgress, statusMenuOpen, setStatusMenuOpen, pollForProcessingStatus } =
    useData();
  const [isMatchingEngineEnabled] = useTreatment(SplitFeatures.MATCHING_ENGINE);
  const showToast = useShowToast();
  const { navigateToImportErrorsTab } = useNavigateToTab();

  const handleCoinsortCompleted = (jobStatus, jobMetaData, battleCardId) => {
    if (!jobMetaData) return;

    const jobProperties = JSON.parse(jobMetaData);
    const isSuccess = jobStatus === JobStatus.COMPLETED;
    const battleCardName = jobProperties.battlecardName;

    updateCoinsortRunningLookupMap(battleCardId, false);
    updateCoinsortProgressLookupMap(battleCardId, { coinsortStatus: jobStatus });
    setStatusMenuOpen(false);

    showToast(
      <div data-testid="coinsort-complete-toast">
        <div style={{ paddingBottom: 6 }}>
          <HTMLHeading styleLevel="h6" tagLevel="h2">
            {isSuccess ? formatMessage('COIN_SORTING_COMPLETED') : formatMessage('COIN_SORTING_FAILED')}
          </HTMLHeading>
          <p>
            {isSuccess
              ? formatMessage('COINSORT_COMPLETED_TOAST_MESSAGE', { battleCardName })
              : formatMessage('COINSORT_FAILED_TOAST_MESSAGE', {
                  quotaComponentName: jobProperties.quotaComponentName,
                  battleCardName
                })}
          </p>
          <Button
            style={{ padding: 0 }}
            text={formatMessage('VIEW_STATUS')}
            minimal
            rightIcon={ArrowRightIcon}
            onClick={() => setStatusMenuOpen(true)}
          />
        </div>
      </div>,
      isSuccess ? Intent.SUCCESS : Intent.DANGER
    );

    getTDR(battleCardId, jobProperties.quotaComponentId);
  };

  const checkCoinsortCompleteStatus = (jobItems) => {
    const completedJobs = jobItems.filter((job) => job.status === JobStatus.COMPLETED).map((job) => job.id);
    const failedJobs = jobItems.filter((job) => job.status === JobStatus.FAILED).map((job) => job.id);
    Object.entries(coinsortRunningLookupMap).forEach(([bcId, jobId]) => {
      const curItem = jobItems.find((job) => job.id === jobId);
      if (!curItem) return;

      if (!!jobId && completedJobs.includes(jobId)) {
        return handleCoinsortCompleted(JobStatus.COMPLETED, curItem.metadata, bcId);
      } else if (!!jobId && failedJobs.includes(jobId)) {
        handleCoinsortCompleted(JobStatus.FAILED, curItem.metadata, bcId);
      }
    });
  };

  useEffect(() => {
    if (jobItems.length > 0) {
      // load coinsortRunningLookupMap with running jobs when the map is empty
      if (Object.keys(coinsortRunningLookupMap).length === 0) {
        jobItems
          .filter((job) => job.isActive)
          .forEach((job) => {
            updateCoinsortRunningLookupMap(JSON.parse(job.metadata || null)?.battlecardId.toString(), job.id);
          });
      }

      // when jobItem changes, check the complete status for coinsort jobs
      checkCoinsortCompleteStatus(jobItems);
    }
  }, [jobItems]);

  const [loadFileList, { data: fileList }] = useGetFileListForStatusMenuLazy({
    variables: {
      planningCycleId: selectedPlanningCycle?.id,

      tableInput: {
        startRow: 1,
        endRow: 20,
        uploadFilter: {
          fileTypes: [
            FileTypeEnum.Activity,
            FileTypeEnum.TerritoryRule,
            FileTypeEnum.CustomHierarchy,
            FileTypeEnum.CustomerAccountHierarchy,
            FileTypeEnum.Location,
            FileTypeEnum.Participant,
            FileTypeEnum.AccountRedirect,
            FileTypeEnum.SellerAssignment,
            FileTypeEnum.TerritoryQuota
          ],
          statuses: [
            FileStatus.CHECKING_PROGRESS,
            FileStatus.PENDING_VALIDATION,
            FileStatus.IN_PROGRESS,
            FileStatus.VALIDATING,
            FileStatus.COMPLETED,
            FileStatus.FAILED
          ]
        }
      }
    },
    fetchPolicy: 'no-cache' // This is to make sure the menu item not overwritten by getFileList called with different params in the manage table page
  });

  const allFileUploads = fileList?.getPlanningCycleSpec?.tables?.tableList || [];
  const fileUploadsLast7Days = allFileUploads.filter((file) => checkWithinLast7Days(new Date(file.updatedAt)));

  const filesInProgress = fileUploadsLast7Days.filter(
    (file) =>
      file.status === FileStatus.IN_PROGRESS ||
      file.status === FileStatus.PENDING_VALIDATION ||
      file.status === FileStatus.VALIDATING ||
      file.status === FileStatus.CHECKING_PROGRESS
  );

  useEffect(() => {
    loadFileList();
  }, [fileUploadInProgress]);

  useEffect(() => {
    if (!selectedPlanningCycle?.id) return;
    if (filesInProgress.length > 0) {
      setIsInitialLoad(false);
    }

    // filesInProgress is the new data coming from the server
    // fileUploadInProgress is the data stored in the data provider
    // and it is used to refetch getFileList to get the latest filesInProgress data
    if (fileUploadInProgress.length === 0 && filesInProgress.length > 0 && isInitialLoad) {
      setFileUploadInProgress(
        filesInProgress.map((file) => ({
          ...file,
          planningCycleId: selectedPlanningCycle?.id,
          tenantId: null,
          message: ''
        }))
      );
      pollForProcessingStatus();
      showToast(
        <ToastMessage
          title={formatMessage('TASKS_RESUMED')}
          message={formatMessage('TASKS_RESUMED_MESSAGE')}
          actionButtonText={formatMessage('VIEW_STATUS')}
          actionButtonOnClick={() => setStatusMenuOpen(true)}
        />,
        'primary'
      );
    }
  }, [selectedPlanningCycle, filesInProgress]);

  const jobsInProgressCount = jobItems.filter((job) => job.isActive || job.status === JobStatus.PENDING).length;
  return (
    <div className={b()} data-testid="status-menu">
      <DropdownMenu
        onOpenChange={(newState) => {
          setStatusMenuOpen(newState);
        }}
        open={statusMenuOpen}
        placement="bottom-end"
        maxWidth="large"
        content={
          <>
            <DropdownMenuHeading data-testid="status-menu-heading">
              <div className={b('header')}>{formatMessage('STATUS')}</div>
            </DropdownMenuHeading>
            <DropdownMenuDivider />
            <div className={b('content')}>
              {isMatchingEngineEnabled ? (
                <Tabs
                  data-testid="status-menu-tabs"
                  id="status-menu-tabs"
                  selectedTabId={activeTabPanel}
                  onChange={(tabId: StatusMenuTab) => setActiveTabPanel(tabId)}
                >
                  <Tab
                    id={StatusMenuTab.COIN_SORTS}
                    title={formatMessage('COIN_SORTS')}
                    panel={CoinSortMenu(jobItems)}
                  />
                  <Tab
                    id={StatusMenuTab.IMPORTS}
                    title={formatMessage('IMPORTS')}
                    panel={FileMenu(fileUploadsLast7Days, navigateToImportErrorsTab)}
                  />
                </Tabs>
              ) : (
                FileMenu(fileUploadsLast7Days, navigateToImportErrorsTab)
              )}
            </div>
          </>
        }
        target={
          pollingJobs || filesInProgress.length > 0 ? (
            <Button className={b('progressBarTag')} minimal data-testid="status-menu-in-progress-btn">
              <Tag rightIcon={<CircleFilled size={24} />}>
                {/* jobs include matching engine progress: coin sorts */}
                {/* files include non-matching engine progress: imports */}
                {jobsInProgressCount + filesInProgress.length > 1 ? (
                  <span className={b('multiTaskMsg')}>
                    {formatMessage('TASKS_IN_PROGRESS', {
                      numOfTasks: jobItems.filter((job) => job.isActive).length + filesInProgress.length
                    })}
                  </span>
                ) : (
                  <>
                    {jobsInProgressCount > 0 && (
                      <AdvancedProgressBar
                        value={jobItems.find((job) => job.isActive).progress / 100}
                        showPercentage
                        intent="primary"
                        formatNumber={(progress) => formatNumber(progress, { style: 'percent' })}
                      />
                    )}
                    {filesInProgress.length > 0 && (
                      <span className={b('multiTaskMsg')}>{formatMessage('IMPORT_IN_PROGRESS')}</span>
                    )}
                  </>
                )}
              </Tag>
            </Button>
          ) : (
            <IconOnlyButton
              className={b('icon')}
              icon={CircleFilled}
              text={null}
              minimal
              outlined={true}
              intent={'success'}
              data-testid="status-menu-btn"
            />
          )
        }
      />
    </div>
  );
};

export default StatusMenu;
